import { useParams } from 'react-router-dom';
import {
  useComponent,
  useEntityLogs,
  useItemProcesses,
  useDepreciationHistory,
} from '@/api/capacities';
import { InventoryDetailView } from '../items/inventory-detail-view';

export interface CapacityComponentDetailPageProps {
  /** Component id. Falls back to the `:id` route param when omitted. */
  id?: string;
}

/**
 * Component detail — identity header (name · ref · status) over Overview
 * (model / supplier / location / purchase & warranty / depreciation) and
 * Activity (component logs → Timeline) tabs. Read-only. Shares the inventory
 * detail view with equipment; a component is keyed on `ref` (not service_tag).
 */
export default function CapacityComponentDetailPage({ id: idProp }: CapacityComponentDetailPageProps) {
  const params = useParams();
  const id = idProp ?? params.id ?? '';

  const { data: component, isLoading, isError, refetch } = useComponent(id);
  const {
    data: logsData,
    isLoading: logsLoading,
    isError: logsError,
    refetch: refetchLogs,
  } = useEntityLogs('components', id);
  const {
    data: processesData,
    isLoading: processesLoading,
    isError: processesError,
    refetch: refetchProcesses,
  } = useItemProcesses(id);
  const {
    data: depreciationData,
    isLoading: depreciationLoading,
    isError: depreciationError,
  } = useDepreciationHistory(id);

  return (
    <InventoryDetailView
      backTo="/capacities/items"
      backLabel="Inventory"
      noun="Component"
      reference={component?.ref ? `#${component.ref}` : undefined}
      item={component}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      logs={logsData?.results ?? []}
      logsLoading={logsLoading}
      logsError={logsError}
      onRetryLogs={() => refetchLogs()}
      processes={processesData?.results ?? []}
      processesLoading={processesLoading}
      processesError={processesError}
      onRetryProcesses={() => refetchProcesses()}
      depreciation={depreciationData?.results ?? []}
      depreciationLoading={depreciationLoading}
      depreciationError={depreciationError}
    />
  );
}
